import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar'; // Keep import if used conditionally
import chroma from 'chroma-js';
import Lottie from 'react-lottie';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Paper, Typography, Tooltip, IconButton, FormControl, InputLabel, Select, MenuItem, TextField, Grid } from '@mui/material';
import * as Icons from '@mui/icons-material';
import { toast } from 'react-toastify';

// Components
import ErrorBoundary from '../../components/ErrorBoundary';
import Loading from '../general-components/Loading';

// Utilities & Data
import animationData from '../../lotties/empty-box';
import nodeAxiosFirebase from '../../utils/nodeAxiosFirebase';

// Style
import 'react-perfect-scrollbar/dist/css/styles.css';


const BlockLayout = ({
  layout,
  elementDetails,
  childrenComponent,
  editMode,
  publicMode,
  activeIndex,
  setDrag,
  handleRemoveBlock,
  handleUpdateBlock,
  handleRefresh,
  structureId: parentStructureId,
  moduleName,
  heightPercentage,
  noPadding,
  fromList,
  blockTypeOptions,
  matchOptions,
  structures,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { structureId: routeStructureId } = useParams();
  const businessStructure = {}
  const singleElement = {}

  const currentLangCode = i18n.language;
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDarkMode = theme.palette.mode === 'dark';
  const blockStructureId = layout?.structureId || parentStructureId;

  const structure = useMemo(
    () => businessStructure?.structures?.find((s) => s.id === blockStructureId),
    [businessStructure?.structures, blockStructureId]
  );

  const structureFields = useMemo(() => structure?.fields || [], [structure]);
  const structureIconName = useMemo(
    () => structure?.icon || 'FolderOutlined',
    [structure]
  );
  const structureName = useMemo(() => structure?.name, [structure]);


  const blockData = useMemo(
    () => ({
      name: elementDetails?.header?.name || '',
      filter: elementDetails?.header?.filter || {},
      labels: elementDetails?.header?.labels ?? true,
      createBtn: elementDetails?.header?.createBtn || false,
      editBtn: elementDetails?.header?.editBtn || false,
      refreshBtn: elementDetails?.header?.refreshBtn || false,
      realtime: elementDetails?.header?.realtime || false,
      icon: elementDetails?.header?.icon || '',
      infos: elementDetails?.header?.infos || {},
      color: layout?.color || 'white',
      bgPattern: layout?.bgPattern || '',
      match: layout?.match || '',
      structureId: blockStructureId,
      i: layout?.i,
      changesInProgress: elementDetails?.header?.changesInProgress || [],
      groups: elementDetails?.groups || [],
    }),
    [elementDetails?.header, elementDetails?.groups, layout, blockStructureId]
  );

  const blockContentData = useMemo(
    () => elementDetails?.data || [],
    [elementDetails?.data]
  );

  const defaultLottieOptions = useMemo(
    () => ({
      loop: true,
      autoplay: true,
      animationData: animationData,
      rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
    }),
    []
  );

  // --- Dynamic Styles ---
  const mainColor = theme.palette.primary.main;
  const secColor = theme.palette.secondary.main;

  const {
    bgColor,
    headerBgColor,
    borderColor,
    fontColor,
    headerTextColor,
    headerIconColor,
  } = useMemo(() => {
    const isSingleContent = false
    const statusColor =
      isSingleContent && layout?.data?.[0]?.typeValue === 'status'
        ? (structureFields
          .find((f) => f.typeData === 'status')
          ?.selections?.find(
            (s) =>
              s[`label_${currentLangCode}`]?.toString() ===
              layout.data[0].transformedValue?.toString()
          )?.color || mainColor) + '30'
        : null;

    let resolvedBgColor = isDarkMode ? '#171717' : '#f6f6f685';
    let resolvedHeaderBgColor = 'transparent';
    let resolvedBorderColor = '#69696930';
    let resolvedFontColor = isDarkMode ? '#fff' : '#000';
    let resolvedHeaderTextColor = resolvedFontColor;
    let resolvedHeaderIconColor = resolvedFontColor;

    const colorMapping = {
      transparent: {
        bg: 'transparent',
        border: '#69696930',
        header: 'transparent',
        font: isDarkMode ? '#fff' : '#000',
      },
      white: {
        bg: isDarkMode ? '#171717' : '#ffffff',
        bgOpacity: isDarkMode ? '' : '90',
        border: '#69696930',
        header: isDarkMode
          ? `linear-gradient(178deg, ${'#0f0f0f'} 25%, ${'#1c1c1c'} 52%, #1c1c1c 75%)`
          : `linear-gradient(178deg, ${'#ffffffb3'} 25%, ${'#fff'} 52%, #FFFFFF 75%)`, // Slight transparency
        font: isDarkMode ? '#fff' : '#000',
      },
      mainColor: {
        bg: isDarkMode ? '#171717' : mainColor,
        bgOpacity: isDarkMode ? '85' : '10',
        border: chroma(mainColor).darken(0.99).hex() + '30',
        header: isDarkMode
          ? `linear-gradient(178deg, ${chroma(mainColor).darken(4).hex() + 'b3'
          } 25%, ${'#0f0f0f'} 52%, #1c1c1c 75%)`
          : `linear-gradient(178deg, ${chroma(mainColor)
            .alpha(0.06)
            .css()} 25%, ${chroma(mainColor)
              .alpha(0.1)
              .css()} 52%, #FFFFFF 75%)`,
        font: isDarkMode ? '#fff' : mainColor, // Use mainColor for text in light mode
        headerText: isDarkMode ? '#fff' : chroma(mainColor).darken(1.5).hex(), // Darker text for header in light mode
      },
      secColor: {
        bg: isDarkMode ? '#171717' : secColor, // Use dark bg, or secColor in light
        bgOpacity: isDarkMode ? '85' : '10', // Apply opacity
        border: chroma(secColor).darken(0.99).hex() + '30',
        header: isDarkMode
          ? `linear-gradient(178deg, ${chroma(secColor).darken(4).hex() + 'b3'
          } 25%, ${'#0f0f0f'} 52%, #1c1c1c 75%)`
          : `linear-gradient(178deg, ${chroma(secColor)
            .alpha(0.09)
            .css()} 25%, ${chroma(secColor)
              .alpha(0.014)
              .css()} 52%, #FFFFFF 75%)`,
        font: isDarkMode ? '#fff' : secColor, // Use secColor for text in light mode
        headerText: isDarkMode ? '#fff' : chroma(secColor).darken(1.5).hex(), // Darker text for header in light mode
      },
    };

    const selectedColorConf =
      colorMapping[blockData.color] || colorMapping['white']; // Fallback to white

    resolvedBgColor = selectedColorConf.bg;
    if (selectedColorConf.bgOpacity && resolvedBgColor !== 'transparent') {
      resolvedBgColor += selectedColorConf.bgOpacity; // Append opacity hex if defined
    }
    // Special case for single content status block
    if (statusColor && isSingleContent) {
      resolvedBgColor = statusColor;
      resolvedFontColor =
        chroma.contrast(statusColor, '#FFF') > 4.5 ? '#fff' : '#000'; // Auto contrast for font
    } else if (
      isSingleContent &&
      blockData.color !== 'white' &&
      blockData.color !== 'transparent'
    ) {
      // For single content with main/sec color bg, use white/black text
      resolvedFontColor = isDarkMode
        ? '#fff'
        : chroma.contrast(selectedColorConf.bg, '#FFF') > 4.5
          ? '#fff'
          : '#000';
    } else {
      resolvedFontColor = selectedColorConf.font;
    }

    resolvedBorderColor = selectedColorConf.border;
    resolvedHeaderBgColor = editMode ? selectedColorConf.header : 'transparent';
    resolvedHeaderTextColor = selectedColorConf.headerText || resolvedFontColor;
    resolvedHeaderIconColor =
      selectedColorConf.headerIcon || resolvedHeaderTextColor;

    return {
      bgColor: resolvedBgColor,
      headerBgColor: resolvedHeaderBgColor,
      borderColor: resolvedBorderColor,
      fontColor: resolvedFontColor,
      iconColor: resolvedFontColor,
      headerTextColor: resolvedHeaderTextColor,
      headerIconColor: resolvedHeaderIconColor,
    };
  }, [
    blockData.color,
    layout?.contentType,
    blockContentData?.length,
    isDarkMode,
    mainColor,
    secColor,
    editMode,
    currentLangCode,
    structureFields,
    layout?.data,
  ]);

  // --- Icon Component ---
  const HeaderIcon = useMemo(
    () => Icons[structureIconName] || Icons.FolderOutlined,
    [structureIconName]
  );

  // --- Event Handlers ---
  const handleChangeBlockSetting = useCallback(
    (key, value, position) => {

      const update = {
        ...layout,
        ...elementDetails,
        name: layout.name || elementDetails?.header?.name || `Block ${layout.i}`,
        name_fr: layout.name_fr || elementDetails?.header?.name_fr || `Block ${layout.i}`,
        name_en: layout.name_en || elementDetails?.header?.name_en || `Block ${layout.i}`,
        x: layout.x,
        y: layout.y,
        w: layout.w,
        h: layout.h,
        minW: layout.minW,
        minH: layout.minH,
        maxW: layout.maxW,
        maxH: layout.maxH,
        static: layout.static,
        isDraggable: layout.isDraggable,
        isResizable: layout.isResizable,
        structureId: layout.structureId,
        contentType: layout.contentType,
        color: layout.color,
        bgPattern: layout.bgPattern,
        match: layout.match,
        filter: layout.filter,
        createBtn: layout.createBtn,
        editBtn: layout.editBtn,
        refreshBtn: layout.refreshBtn,
        realtime: layout.realtime,
        icon: layout.icon,
        infos: layout.infos || {},
        groups: layout.groups || [],
        data: layout.data || [],
        price: layout.price || 0,
        [key]: value,
      };



      if (key === 'name') {
        update.name = value;
        update.name_fr = value;
        update.name_en = value;
      }


      if (position === 'header') {
        update.header = {
          ...elementDetails?.header,
          [key]: value,
          name: layout.name || elementDetails?.header?.name || `Block ${layout.i}`,
          name_fr: layout.name_fr || elementDetails?.header?.name_fr || `Block ${layout.i}`,
          name_en: layout.name_en || elementDetails?.header?.name_en || `Block ${layout.i}`,
        };
      }

      if (key === 'price') {
        update.price = parseFloat(value) || 0;
      } else if (key === 'infos') {
        update.infos = {
          ...layout.infos,
          ...value,
        };
      }

      handleUpdateBlock(update, layout?.i);
    },
    [layout, elementDetails, handleUpdateBlock]
  );

  const handleToggleCreateBtn = useCallback(() => {
    handleChangeBlockSetting('createBtn', !blockData.createBtn, 'header');
  }, [handleChangeBlockSetting, blockData.createBtn]);

  const handleToggleEditBtn = useCallback(() => {
    handleChangeBlockSetting('editBtn', !blockData.editBtn, 'header');
  }, [handleChangeBlockSetting, blockData.editBtn]);

  const handleToggleInfo = useCallback(() => {
    const newInfo = {
      display: !blockData.infos?.display,
      text: blockData.infos?.text || '',
    };
    handleChangeBlockSetting('infos', newInfo, 'header');
  }, [blockData.infos, handleChangeBlockSetting]);

  const openCreateElementModal = useCallback((defaultValues) => {
    if (!blockStructureId) {
      toast.error(t('cannotCreateElementNoStructure'));
      return;
    }


    const match = layout?.match?.split(':')[0];



  }, [
    dispatch,
    t,
    layout?.type,
    layout?.match,
    layout?.i,
    blockStructureId,
    routeStructureId,
    singleElement,
    activeIndex,

    handleRefresh,
  ]);



  const openEditElementDrawer = useCallback(() => {
    const primaryDataSource = blockContentData?.find(
      (item) => item.primaryData
    );
    const elementToEdit = primaryDataSource?.primaryData;


    toast.info(t('noEditableElementFound'));

  }, [dispatch, blockContentData, handleRefresh, t]);





  // --- Helper Functions ---
  const shouldDisplayHeader = useMemo(() => {
    return (
      editMode ||
      !(layout?.contentType === 'content' && blockContentData?.length === 1)
    );
  }, [editMode, layout?.contentType, blockContentData?.length]);

  // --- Render ---
  const isLoadingBlock = false

  return (
    <ErrorBoundary>
      <Paper
        elevation={editMode ? 3 : 1}
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: bgColor,
          border: `1px solid ${borderColor}`,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {shouldDisplayHeader && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px',
              background: headerBgColor,
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            {/* Left side: Icon, Title, Dev Info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                flexShrink: 1,
              }}
            >
              {isLoadingBlock ? (
                <Loading size={'small'} type={'circle'} sx={{ ml: 0.5 }} />
              ) : blockData.realtime ? (
                <Tooltip title={t('realtimeData')}>
                  <Icons.PodcastsOutlined
                    sx={{
                      fontSize: isTablet ? '14px' : '16px',
                      color: headerIconColor,
                      mr: 1,
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip title={structureName || t('blockType')}>
                  <HeaderIcon
                    sx={{
                      fontSize: isTablet ? '14px' : '16px',
                      color: headerIconColor,
                      mr: 1,
                    }}
                  />
                </Tooltip>
              )}

              {editMode ? (
                <input
                  value={blockData.name}
                  onChange={(e) =>
                    handleChangeBlockSetting('name', e.target.value, 'header')
                  }
                  placeholder={t('blockNamePlaceholder')}
                  style={{
                    flexGrow: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontWeight: 600,
                    fontSize: isTablet ? '12px' : '14px',
                    color: headerTextColor,
                    padding: '2px 4px',
                    marginRight: '8px',
                  }}
                />
              ) : (
                <Typography
                  noWrap
                  sx={{
                    fontWeight: 600,
                    fontSize: isTablet ? '12px' : '14px',
                    color: headerTextColor,
                    mr: 1,
                  }}
                >
                  {blockData.name || t('newBlock')}
                </Typography>
              )}

              {editMode ? (
                <Tooltip title={t('editInfo')}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleInfo();
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ p: '4px' }}
                  >
                    <Icons.InfoOutlined
                      sx={{
                        fontSize: '14px',
                        color: blockData.infos?.display ? headerIconColor : '#cccccc',
                      }}
                    />
                  </IconButton>
                </Tooltip>
              ) : blockData.infos?.display && (
                <Tooltip title={blockData.infos.text || ''}>
                  <Icons.InfoOutlined
                    sx={{
                      fontSize: '14px',
                      color: headerIconColor,
                      cursor: 'default',
                    }}
                  />
                </Tooltip>
              )}

              {editMode && blockData.infos?.display && (
                <input
                  value={blockData.infos.text || ''}
                  onChange={(e) => {
                    e.stopPropagation();
                    const newInfo = {
                      display: true,
                      text: e.target.value,
                    };
                    handleChangeBlockSetting('infos', newInfo, 'header');
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  placeholder={t('blockInfoPlaceholder')}
                  style={{
                    flexGrow: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: isTablet ? '11px' : '12px',
                    color: headerTextColor,
                    padding: '2px 4px',
                    marginRight: '8px',
                    fontStyle: 'italic',
                  }}
                />
              )}
            </div>

            {/* Right side: Actions & Status Icons */}
            <div
              style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              {(blockData.createBtn || editMode) &&
                childrenComponent !== 'content' && (
                  <Tooltip title={t('createNewElement')}>
                    <IconButton
                      size="small"
                      onClick={
                        editMode
                          ? handleToggleCreateBtn
                          : openCreateElementModal
                      }
                      sx={{ p: '4px' }}
                    >
                      <Icons.AddOutlined
                        sx={{
                          fontSize: '18px',
                          color: editMode
                            ? blockData.createBtn
                              ? headerIconColor
                              : '#cccccc'
                            : headerIconColor,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}

              {(blockData.editBtn || editMode) &&
                childrenComponent === 'content' && (
                  <Tooltip title={t('editElement')}>
                    <IconButton
                      size="small"
                      onClick={
                        editMode ? handleToggleEditBtn : openEditElementDrawer
                      }
                      sx={{ p: '4px' }}
                    >
                      <Icons.EditOutlined
                        sx={{
                          fontSize: '16px',
                          color: editMode
                            ? blockData.editBtn
                              ? headerIconColor
                              : '#cccccc'
                            : headerIconColor,
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}


              {blockData.filter?.operator && !editMode && (
                <Tooltip title={t('filterActive')}>
                  <span>
                    <IconButton size="small" disabled sx={{ p: '4px' }}>
                      <Icons.FilterListOutlined
                        sx={{ fontSize: '16px', color: headerIconColor }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {blockData.order?.direction && !editMode && (
                <Tooltip
                  title={`${t(blockData.order.direction + 'Order')} - ${structureFields.find(
                    (f) => f.value === blockData.order.field
                  )?.name ||
                    t(blockData.order.field) ||
                    ''
                    }`}
                >
                  <span>
                    <IconButton size="small" disabled sx={{ p: '4px' }}>
                      {blockData.order.direction === 'asc' ? (
                        <Icons.ArrowUpward
                          sx={{ fontSize: '16px', color: headerIconColor }}
                        />
                      ) : (
                        <Icons.ArrowDownward
                          sx={{ fontSize: '16px', color: headerIconColor }}
                        />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {blockData.changesInProgress?.some(
                (c) => c.tab === activeIndex && c.block === layout?.i
              ) &&
                !editMode && (
                  <Tooltip title={t('changesInProgress')}>
                    <Icons.HourglassBottom
                      sx={{ fontSize: '16px', color: headerIconColor, ml: 0.5 }}
                    />
                  </Tooltip>
                )}


            </div>
          </div>
        )}

        {/* Block Content */}
        <div
          style={{
            flexGrow: 1,
            overflow: 'hidden',
            position: 'relative',
            background: !shouldDisplayHeader || headerBgColor === 'transparent' ? bgColor : 'transparent',
            border: `1px solid ${borderColor}`,
            borderRadius: editMode ? '0 0 8px 8px' : '8px',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: !shouldDisplayHeader ? 'flex-start' : undefined,
            alignItems: !shouldDisplayHeader ? 'flex-start' : undefined,
          }}
        >
          {editMode && (
            <Grid container spacing={2} sx={{ p: 2 }}>
              <Grid item xs={12} sm={6} sx={{ width: '30%' }}>
                <FormControl fullWidth>
                  <InputLabel shrink>Type</InputLabel>
                  <Select
                    key={layout?.i + activeIndex + 'type'}
                    value={layout.contentType || 'content'}
                    onChange={(e) => handleChangeBlockSetting('contentType', e.target.value, 'layout')}
                  >
                    {blockTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ width: '30%' }}>
                <FormControl fullWidth>
                  <InputLabel shrink>Match</InputLabel>
                  <Select
                    key={layout?.i + activeIndex + 'match'}
                    value={layout?.match || ''}
                    onChange={(e) => handleChangeBlockSetting('match', e.target.value, 'layout')}
                  >
                    {matchOptions?.map((option) => (
                      <MenuItem key={option?.value} value={option?.value}>
                        {option?.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ width: '32%' }}>
                <FormControl fullWidth>
                  <InputLabel shrink>Structure</InputLabel>
                  <Select
                    key={layout?.i + activeIndex + 'structure'}
                    value={layout.structureId || ''}
                    onChange={(e) => handleChangeBlockSetting('structureId', e.target.value, 'layout')}
                  >
                    <MenuItem value="">None</MenuItem>
                    {structures.map((structure) => (
                      <MenuItem key={structure.id} value={structure.id}>
                        {structure.name_fr || structure.name_en || structure.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ width: '100%' }}>
                <FormControl fullWidth>
                  <InputLabel shrink>Prix</InputLabel>
                  <TextField
                    key={layout?.i + activeIndex + 'price'}
                    type="number"
                    value={layout.price || ''}
                    onChange={(e) => {
                      const newPrice = e.target.value ? parseFloat(e.target.value) : 0;
                      handleChangeBlockSetting('price', newPrice, 'layout');
                    }}
                    inputProps={{
                      min: 0,
                      step: 0.01
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>
          )}

          {childrenComponent === 'list' &&
            blockContentData?.length === 0 &&
            !isLoadingBlock ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                padding: '10px',
                textAlign: 'center',
              }}
            >
              <Lottie
                options={defaultLottieOptions}
                height={isTablet ? 40 : 60}
                width={isTablet ? 50 : 80}
              />
              <Typography
                variant="h6"
                sx={{ fontSize: isTablet ? '11px' : '13px', mt: 1 }}
              >
                {t('emptyListTitle')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: isTablet ? '9px' : '10px',
                  mt: 0.5,
                  color: 'text.secondary',
                }}
              >
                {t('emptyListDescription')}
              </Typography>
            </div>
          ) : (
            <div style={{ flexGrow: 1, overflow: 'hidden', width: '100%' }}>
              <PerfectScrollbar
                options={{ wheelPropagation: false, suppressScrollX: true }}
                style={{
                  height: '100%',
                  width: '100%',
                  padding:
                    noPadding || layout?.contentType === 'action'
                      ? 0
                      : shouldDisplayHeader
                        ? '8px'
                        : '12px',
                }}
              >
                {/* ... existing content code ... */}
              </PerfectScrollbar>
            </div>
          )}
        </div>
      </Paper>
    </ErrorBoundary>
  );
};

export default BlockLayout;
